<template>
  <div id="app">
    <Layout>
      <template #router>
        <router-view />
      </template>
    </Layout>
  </div>
</template>

<script>
import '@/assets/css/line.css';
import '@/assets/css/style.css';
import '@/assets/css/date.css';
import Layout from '@/layouts';
import { ElMessage } from 'element-plus';
import waitingRoomApi from '@/api/waiting-room.js';

let websocketRetryCount = 0;
const MAX_RETRY = 3;
let appSocket = null;
const PING_INTERVAL = 7000 // 7s

export default {
  name: "App",
  components: {
    Layout,
  },

  data() {
    return {
      hideTimer: null,
      hasTimedOut: false,
      pingInterval: null,
      socketOnlineClosedFlag: false,
    };
  },

  computed: {
    isInWaitingRoom() {
      const currentRoute = this.$route?.path;
      const waitingRoom = sessionStorage.getItem("waitingRoom");
      return currentRoute === "/waiting-room" || waitingRoom === "true";
    },
  },

  watch: {
    $route() {
      const shouldConnect = sessionStorage.getItem("shouldConnectSocket");
      if (shouldConnect === "true") {
        this.connectWebSocket();
        sessionStorage.removeItem("shouldConnectSocket");
      }
    },
  },

  mounted() {
    // Web
    window.addEventListener("beforeunload", this.handleUserLeaveApp);

    // LINE App
    document.addEventListener("visibilitychange", this.handleVisibilityChange);
  },
  unmounted() {
    window.removeEventListener("beforeunload", this.handleUserLeaveApp);

    document.removeEventListener("visibilitychange", this.handleVisibilityChange);

    // Clear any existing timer when component is unmounted
    this.clearHideTimer();

    // Clear ping websocket App.vue
    this.clearPingInterval();
  },

  methods: {
    isInLineApp() {
      return navigator.userAgent.includes("Line");
    },

    // Function to handle LIFF login
    async liffLogin() {
      if (!liff.isLoggedIn()) {
        await liff.login();
      }
    },

    // Call API Offline
    handleUserLeaveApp() {
      this.sendOfflineStatus();
    },

    connectWebSocket() {
      if (this.isInWaitingRoom) {
        return;
      }

      // Clear existing ping interval before creating new connection
      this.clearPingInterval();
      
      const userId = sessionStorage.getItem("openId");
      const wsUrl = `${process.env.WEBSOCKET_APP_URL}?userId=${userId}`;

      // Don't create new WebSocket if already connecting or connected
      if (
        appSocket &&
        (appSocket.readyState === WebSocket.OPEN ||
          appSocket.readyState === WebSocket.CONNECTING)
      ) {
        return;
      }
      appSocket = new WebSocket(wsUrl);

      appSocket.onopen = () => {
        websocketRetryCount = 0;
        console.log('✅ App.vue WebSocket connected');

        this.pingInterval = setInterval(() => {
          if (appSocket && appSocket.readyState === WebSocket.OPEN) {
            appSocket.send('ping');
            console.log('📡 Ping sent from App.vue');
          }
        }, PING_INTERVAL);
      };

      appSocket.onmessage = (event) => {
        console.log("App.vue received message");
      };

      appSocket.onerror = (error) => {
        console.error('App.vue WebSocket error:', error);
        const userId = sessionStorage.getItem('openId');
        websocketRetryCount++;
        if (websocketRetryCount < MAX_RETRY) {
          setTimeout(() => {
            this.connectWebSocket();
          }, 1000); 
        } else {
          // Call API remove online user from DynamoDB
          (async () => {
            await waitingRoomApi.removeOnlineUser({ userId });
          })();
        }
      };

      appSocket.onclose = () => {
        console.warn("App.vue WebSocket closed");
        // Stop ping
        this.clearPingInterval();
      };
    },

    handleVisibilityChange() {
      if (!this.isInLineApp()) return;

      if (document.visibilityState === "hidden") {
        // Delete old timer if any
        this.clearHideTimer();

        this.hideTimer = setTimeout(() => {
          this.hasTimedOut = true; // marked as expired
          this.sendOfflineStatus();
        }, parseInt(process.env.VUE_APP_TIMEOUT));
      } else if (document.visibilityState === "visible") {
        // Cancel timer if user returns early
        this.clearHideTimer();
        if (this.hasTimedOut) {
          // If it's been over timeout period, check slot
          this.hasTimedOut = false; // Reset Flag
          this.handleCheckSlot();
        }
      }
    },

    async handleCheckSlot() {
      const userExists = sessionStorage.getItem('userExists');
      const userId = sessionStorage.getItem('openId');
      let accessToken = null;

      if (!liff.id || !liff.isLoggedIn()) {
        try {
          await this.liffLogin();
        } catch (error) {
          console.log('LIFF login failed:', error);
        }
      }

      accessToken = liff.getAccessToken();

      sessionStorage.setItem('shouldConnectSocket', 'false');
      sessionStorage.setItem('waitingRoom', 'false');

      if (userExists === 'true') {
        try {
          const result = await waitingRoomApi.checkSlot({ userId, lineToken: accessToken });
          if (result && result.status === 'error') {
            sessionStorage.setItem('waitingRoom', 'false');
            sessionStorage.setItem('shouldConnectSocket', 'false');
          }
          if (result && result.status === 'allowed') {
            sessionStorage.setItem('waitingRoom', 'false');
            sessionStorage.setItem('shouldConnectSocket', 'true');
            this.connectWebSocket();
          }
          if (result && result.status === 'wait') {
            sessionStorage.setItem('waitingRoom', 'true');
            sessionStorage.setItem('shouldConnectSocket', 'false');
            window.location.href = '/waiting-room';
            return;
          }
        } catch (error) {
          console.error('checkSlot API error:', error);
          sessionStorage.setItem('waitingRoom', 'false');
          sessionStorage.setItem('shouldConnectSocket', 'false');
        }
      }
      // (userExists = false) - no redirect
    },

    clearHideTimer() {
      if (this.hideTimer) {
        clearTimeout(this.hideTimer);
        this.hideTimer = null;
      }
    },

    clearPingInterval() {
      if (this.pingInterval) {
        clearInterval(this.pingInterval);
        this.pingInterval = null;
      }
    },

    async sendOfflineStatus() {
      try {
        const userId = sessionStorage.getItem('openId');
        if (appSocket && appSocket.readyState === WebSocket.OPEN) {
          appSocket.close();
          this.socketOnlineClosedFlag = true;
        }
        if (window.__globalSocket && window.__globalSocket.readyState === WebSocket.OPEN) {
          window.__globalSocket.close();
          this.socketOnlineClosedFlag = true;
        }
        // Call API remove online user by userId from DynamoDB
        if (!this.socketOnlineClosedFlag) {
          navigator.sendBeacon(
           `${process.env.WAITING_ROOM_API_BASE_URL}/remove-slot`,
           JSON.stringify({ userId })
         );
        }
        // Clear ping interval
        this.clearPingInterval();
      } catch (e) {
        console.error("Error Close WebSocket:", e);
      }
    },
  },
};
</script>

<style lang="scss">
@use "@/assets/scss/global.scss" as *;

#app {
  box-sizing: border-box;
  margin: 0;
  padding: 0;
}
.el-scrollbar {
  > .el-scrollbar__bar {
    opacity: 1 !important;
  }
}
</style>
